import React from 'react';
import PropTypes from 'prop-types';
import className from 'classnames';
import { useTranslation } from 'react-i18next';
import { ABUtils, Config } from '../../utils';

function getInputGroupClassName({ errorMessage, error, label }) {
    return className({
        'prisma-input-group': true,
        'odf-space-outer-top-xs': !!label,
        'invalid odf-space-outer-bottom-l': (!!errorMessage || error),
    });
}

function getInputClassName() {
    return className({
        'prisma-input': true,
    });
}

function getSelectStyle(isDisabled) {
    return {
        ...(isDisabled ? { color: '#AAAAAA' } : {}),
        minWidth: 100,
        appearance: 'none',
        WebkitAppearance: 'none',
        backgroundColor: 'transparent',
    };
}

function getArrowIconDivStyle(iconStyle, hasError) {
    return {
        ...iconStyle,
        visibility: hasError ? 'hidden' : 'visible',
        cursor: 'pointer',
        pointerEvents: 'auto'
    };
}

function getErrorIconDivStyle(iconStyle, hasError) {
    return {
        ...iconStyle,
        visibility: hasError ? 'visible' : 'hidden'
    };
}

function getErrorMessageStyle(hasError) {
    return {
        visibility: hasError ? 'visible' : 'hidden',
        minHeight: hasError ? 'auto' : '1em'
    };
}

const AgeField = ({ errorMessage, error, id, label, value, isDisabled, min, max, onChange }) => {
    const { t } = useTranslation();
    const { isCssVariationMobile } = ABUtils.getABs();
    const direction = Config.get('isRtl') ? 'rtl' : 'ltr';
    const iconStyle = direction === 'rtl' ? { left: '-16px' } : { right: '-16px' };

    const inputGroupClassName = getInputGroupClassName({
        errorMessage,
        error,
        label,
    });
    const inputClassName = getInputClassName();

    const hasError = !!errorMessage || error;
    const selectStyle = getSelectStyle(isDisabled);
    const arrowIconDivStyle = getArrowIconDivStyle(iconStyle, hasError);
    const errorIconDivStyle = getErrorIconDivStyle(iconStyle, hasError);
    const errorMessageStyle = getErrorMessageStyle(hasError);

    return (
        <div style={{ width: isCssVariationMobile ? 'auto' : '100%' }}>
            <label className="odf-text-md" style={{ textTransform: 'capitalize' }} htmlFor={id}>
                {label}
            </label>
            <div className={inputGroupClassName}>
                <select
                    id={id}
                    data-testid={id}
                    className={inputClassName}
                    value={value}
                    readOnly
                    disabled={isDisabled}
                    onChange={({ target }) => onChange(target.value)}
                    style={selectStyle}
                >
                    <option value="">-</option>
                    {[...Array(max + 1 - min)].map((_, index) => (
                        <option value={index + min} key={`age_${index + min}`}>
                            {index + min}
                        </option>
                    ))}
                </select>
                <>
                    <div className="prisma-label prisma-label-valid">
                        <span className="prisma-inner-label">
                            {t('paxSelector.trains.age')}
                        </span>
                    </div>
                    <div
                        className="prisma-form-icons"
                        style={arrowIconDivStyle}
                        onClick={() => document.getElementById(id)?.click()}
                    >
                        <span
                            className="odf-space-outer-right-m prisma-input-icon prisma-input-icon-arrow
                                odf-icon odf-icon-arrow-down"
                            style={{ opacity: 1, color: '#B2B2B2', pointerEvents: 'none' }}
                        />
                    </div>
                    <div
                        className="prisma-form-icons"
                        style={errorIconDivStyle}
                    >
                        <span
                            className="odf-space-outer-right-m prisma-input-icon prisma-input-icon-clear
                                odf-icon odf-icon-cross-circle-light"
                        />
                        <span
                            className="odf-space-outer-right-m prisma-input-icon prisma-input-icon-valid
                                odf-icon odf-icon-tick-circle"
                        />
                        <span
                            className="odf-space-outer-right-m prisma-input-icon prisma-input-icon-error
                                odf-icon odf-icon-exclamation-circle"
                        />
                    </div>
                    <div
                        className="prisma-labels odf-text-sm"
                        data-testid="room-selector-children-error"
                        style={errorMessageStyle}
                    >
                        <div className="odf-row-fluid odf-strong prisma-label-error">
                            {errorMessage || ''}
                        </div>
                    </div>
                </>
            </div>
        </div>
    );
};

AgeField.propTypes = {
    errorMessage: PropTypes.string,
    error: PropTypes.bool,
    id: PropTypes.string.isRequired,
    label: PropTypes.string,
    value: PropTypes.string.isRequired,
    isDisabled: PropTypes.bool,
};

AgeField.defaultProps = {
    errorMessage: '',
    error: false,
    label: '',
    isDisabled: false,
};

export default AgeField;
